import PropTypes from 'prop-types';
import React from 'react';
import {defineMessages, useIntl} from 'react-intl';

import Modal from '../../containers/modal.jsx';
import domiCarIcon from '../../lib/libraries/extensions/domiCar/domi-car-small.svg';
import microBitIcon from '../../lib/libraries/extensions/microbit/microbit-small.svg';

import styles from './burn-center-device-selector.css';

const messages = defineMessages({
    title: {
        id: 'gui.burnCenter.deviceSelector.title',
        defaultMessage: 'Choose a device to burn',
        description: 'Title of the burn center device selector'
    },
    intro: {
        id: 'gui.burnCenter.deviceSelector.intro',
        defaultMessage: 'Choose from the hardware extensions currently loaded in this project.',
        description: 'Introduction in the burn center device selector'
    },
    microbit: {
        id: 'gui.burnCenter.deviceSelector.microbit',
        defaultMessage: 'micro:bit',
        description: 'micro:bit option in the burn center device selector'
    },
    microbitDescription: {
        id: 'gui.burnCenter.deviceSelector.microbitDescription',
        defaultMessage: 'Compile an offline program and write it over USB.',
        description: 'micro:bit description in the burn center device selector'
    },
    domiCar: {
        id: 'gui.burnCenter.deviceSelector.domiCar',
        defaultMessage: 'Domi Car',
        description: 'Domi Car option in the burn center device selector'
    },
    domiCarDescription: {
        id: 'gui.burnCenter.deviceSelector.domiCarDescription',
        defaultMessage: 'Check and write the car offline program.',
        description: 'Domi Car description in the burn center device selector'
    },
    emptyTitle: {
        id: 'gui.burnCenter.deviceSelector.emptyTitle',
        defaultMessage: 'No burnable hardware extension is loaded.',
        description: 'Empty state title in the burn center device selector'
    },
    emptyHelp: {
        id: 'gui.burnCenter.deviceSelector.emptyHelp',
        defaultMessage: 'First add the micro:bit or Domi Car extension, then return to the Burn Center.',
        description: 'Empty state help in the burn center device selector'
    },
    close: {
        id: 'gui.burnCenter.deviceSelector.close',
        defaultMessage: 'Close',
        description: 'Button that closes the burn center device selector'
    }
});

const DEVICE_DETAILS = {
    microbit: {
        description: messages.microbitDescription,
        icon: microBitIcon,
        label: messages.microbit
    },
    domiCar: {
        description: messages.domiCarDescription,
        icon: domiCarIcon,
        label: messages.domiCar
    }
};

const DeviceOption = ({description, device, icon, label, onSelect}) => (
    <button
        aria-label={label}
        className={styles.deviceButton}
        type="button"
        value={device}
        onClick={onSelect}
    >
        <img
            alt=""
            className={styles.deviceIcon}
            draggable={false}
            src={icon}
        />
        <span className={styles.deviceCopy}>
            <strong>{label}</strong>
            <span>{description}</span>
        </span>
        <span
            aria-hidden="true"
            className={styles.arrow}
        >›</span>
    </button>
);

DeviceOption.propTypes = {
    description: PropTypes.string.isRequired,
    device: PropTypes.oneOf(['domiCar', 'microbit']).isRequired,
    icon: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    onSelect: PropTypes.func.isRequired
};

const BurnCenterDeviceSelector = ({
    availableDevices = [],
    isOpen = false,
    onClose,
    onSelect
}) => {
    const intl = useIntl();
    if (!isOpen) return null;

    return (
        <Modal
            className={styles.modal}
            contentLabel={intl.formatMessage(messages.title)}
            id="burn-center-device-selector"
            onRequestClose={onClose}
        >
            <div className={styles.body}>
                <p className={styles.intro}>{intl.formatMessage(messages.intro)}</p>

                {availableDevices.length ? (
                    <div className={styles.deviceGrid}>
                        {availableDevices.map(device => {
                            const details = DEVICE_DETAILS[device];
                            if (!details) return null;
                            const label = intl.formatMessage(details.label);
                            return (
                                <DeviceOption
                                    description={intl.formatMessage(details.description)}
                                    device={device}
                                    icon={details.icon}
                                    key={device}
                                    label={label}
                                    onSelect={onSelect}
                                />
                            );
                        })}
                    </div>
                ) : (
                    <div className={styles.emptyState}>
                        <strong>{intl.formatMessage(messages.emptyTitle)}</strong>
                        <span>{intl.formatMessage(messages.emptyHelp)}</span>
                    </div>
                )}

                <div className={styles.actions}>
                    <button
                        className={styles.closeButton}
                        type="button"
                        onClick={onClose}
                    >
                        {intl.formatMessage(messages.close)}
                    </button>
                </div>
            </div>
        </Modal>
    );
};

BurnCenterDeviceSelector.propTypes = {
    availableDevices: PropTypes.arrayOf(PropTypes.oneOf(['domiCar', 'microbit'])),
    isOpen: PropTypes.bool,
    onClose: PropTypes.func.isRequired,
    onSelect: PropTypes.func.isRequired
};

export default BurnCenterDeviceSelector;
